import type { ApiClient } from '@nyce/admin-kit/api';
import type { ClusterEnvironment } from '../api';
import { client } from '../session';

// ---------- Database overview (M5, docs/contracts.md) ----------

export interface DbServerRef {
  id: string;
  name: string;
  environment: ClusterEnvironment;
  /** Never a copy/restore target. */
  protected: boolean;
  enabled: boolean;
  /** Linked Cloud SQL / AlloyDB instance (resource name); null = none. */
  instance?: string | null;
}

export interface DbDatabase {
  name: string;
  sizeBytes: number;
  connections: number;
  xidAge: number;
  environmentIds: string[];
}

export interface DbOverview {
  version: string;
  startedAt: string;
  maxConnections: number;
  connections: { total: number; active: number; idle: number; idleInTransaction: number };
  databases: DbDatabase[];
}

export interface DbSession {
  pid: number;
  database: string | null;
  user: string | null;
  application: string | null;
  clientAddr: string | null;
  state: string | null;
  waitEventType: string | null;
  waitEvent: string | null;
  backendStart: string | null;
  xactStart: string | null;
  queryStart: string | null;
  durationMs: number | null;
  query: string | null;
  blockedBy: number[];
}

export interface DbTable {
  schema: string;
  name: string;
  rowEstimate: number;
  totalBytes: number;
  tableBytes: number;
  indexBytes: number;
  deadTuples: number;
  lastVacuum: string | null;
  lastAutovacuum: string | null;
  lastAnalyze: string | null;
}

export type BackupKind = 'backup' | 'download';

export interface DbBackup {
  object: string;
  sizeBytes: number;
  createdAt: string;
  kind: BackupKind;
  jobId: number | null;
}

// ---------- Database jobs (M6) ----------

export type JobKind = 'backup' | 'copy';
export type JobStatus = 'queued' | 'running' | 'succeeded' | 'failed' | 'cancelled';
export type JobMethod = 'pg_dump' | 'pg_dump_restore' | 'pgcopydb';
export type CopyMethod = Exclude<JobMethod, 'pg_dump'>;

export interface Job {
  id: number;
  kind: JobKind;
  status: JobStatus;
  /** 0–100 */
  progress: number | null;
  serverId: string;
  database: string;
  targetServerId: string | null;
  targetDatabase: string | null;
  configId: string | null;
  scheduleId: string | null;
  method: JobMethod;
  /** GCS object of a backup. */
  object: string | null;
  sizeBytes: number | null;
  createdBy: string;
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
  error: string | null;
}

export const isActive = (s: JobStatus) => s === 'queued' || s === 'running';

export type LogStream = 'out' | 'err' | 'info';

export interface LogLine {
  seq: number;
  at: string;
  stream: LogStream;
  line: string;
}

export interface PostRestoreScript {
  name: string;
  sql: string;
}

export interface CopyConfig {
  id: string;
  name: string;
  description: string;
  sourceServerId: string;
  sourceDatabase: string;
  targetServerId: string;
  targetDatabase: string;
  method: CopyMethod;
  postRestoreScripts: PostRestoreScript[];
  updatedAt: string;
  updatedBy: string;
}
export type CopyConfigInput = Omit<CopyConfig, 'id' | 'updatedAt' | 'updatedBy'>;

export interface Schedule {
  id: string;
  name: string;
  kind: JobKind;
  /** 5-field cron. */
  cron: string;
  /** IANA time zone. */
  timezone: string;
  backup: { serverId: string; database: string } | null;
  configId: string | null;
  enabled: boolean;
  nextRunAt: string | null;
  lastRunAt: string | null;
  lastJobId: number | null;
  updatedAt: string;
  updatedBy: string;
}
export type ScheduleInput = Omit<
  Schedule,
  'id' | 'nextRunAt' | 'lastRunAt' | 'lastJobId' | 'updatedAt' | 'updatedBy'
>;

export interface SignedUrl {
  url: string;
  expiresAt: string;
}

export interface JobQuery {
  limit?: number;
  /** Id cursor: only jobs older than this id. */
  before?: number;
  kind?: JobKind;
}

/** Target database names the API accepts (a plain Postgres identifier). */
export const DB_IDENT = /^[a-z_][a-z0-9_]{0,62}$/;

const seg = (id: string | number) => encodeURIComponent(String(id));

function query(params: Record<string, string | number | undefined | null>): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v != null && v !== '') q.set(k, String(v));
  const s = q.toString();
  return s ? `?${s}` : '';
}

/** `/db`, `/jobs`, `/copy-configs` and `/schedules` over the shared client. */
export function dbApi(c: ApiClient) {
  return {
    servers: () => c.get<DbServerRef[]>('/db/servers'),
    overview: (serverId: string) => c.get<DbOverview>(`/db/servers/${seg(serverId)}/overview`),
    sessions: (serverId: string, database?: string) =>
      c.get<DbSession[]>(`/db/servers/${seg(serverId)}/sessions${query({ database })}`),
    cancelSession: (serverId: string, pid: number) =>
      c.post<{ ok: boolean }>(`/db/servers/${seg(serverId)}/sessions/${pid}/cancel`),
    terminateSession: (serverId: string, pid: number) =>
      c.post<{ ok: boolean }>(`/db/servers/${seg(serverId)}/sessions/${pid}/terminate`),
    databases: (serverId: string) => c.get<string[]>(`/db/servers/${seg(serverId)}/databases`),
    tables: (serverId: string, database: string, limit = 50) =>
      c.get<DbTable[]>(`/db/servers/${seg(serverId)}/databases/${seg(database)}/tables${query({ limit })}`),
    backups: (serverId: string, database?: string) =>
      c.get<DbBackup[]>(`/db/backups${query({ serverId, database })}`),
    liveDownload: (serverId: string, database: string) =>
      c.post<SignedUrl>(`/db/servers/${seg(serverId)}/databases/${seg(database)}/live-download`),
    jobs: {
      list: (q: JobQuery = {}) =>
        c.get<Job[]>(`/jobs${query({ limit: q.limit ?? 50, before: q.before, kind: q.kind })}`),
      get: (id: number) => c.get<Job>(`/jobs/${id}`),
      log: (id: number, after = 0) => c.get<LogLine[]>(`/jobs/${id}/log${query({ after })}`),
      streamPath: (id: number, after: number) => `/jobs/${id}/stream${query({ after })}`,
      backup: (serverId: string, database: string) => c.post<Job>('/jobs/backup', { serverId, database }),
      copy: (configId: string, confirm: string) => c.post<Job>('/jobs/copy', { configId, confirm }),
      cancel: (id: number) => c.post<Job>(`/jobs/${id}/cancel`),
      download: (id: number) => c.post<SignedUrl>(`/jobs/${id}/download`),
    },
    copyConfigs: {
      list: () => c.get<CopyConfig[]>('/copy-configs'),
      put: (id: string, body: CopyConfigInput) => c.put<CopyConfig>(`/copy-configs/${seg(id)}`, body),
      remove: (id: string) => c.del<void>(`/copy-configs/${seg(id)}`),
    },
    schedules: {
      list: () => c.get<Schedule[]>('/schedules'),
      put: (id: string, body: ScheduleInput) => c.put<Schedule>(`/schedules/${seg(id)}`, body),
      remove: (id: string) => c.del<void>(`/schedules/${seg(id)}`),
      run: (id: string) => c.post<Job>(`/schedules/${seg(id)}/run`),
    },
  };
}

export type DbApi = ReturnType<typeof dbApi>;

export const db = dbApi(client);
